/**
 * „Numery protokołów" (A22, lista Bronka 2026-10) — czyste helpery modala: lista adres → numer
 * sprawy CEEB (z rokiem), tekst do schowka i CSV dla Excela. Dane: POST /buildings/case-numbers
 * (backend wybiera rok: bieżący, a bez niego ostatni znany).
 *
 * Numery są UNIKALNE (ten sam numer przy dwóch rekordach budynku — np. duplikat adresu — trafia na
 * listę raz, przy pierwszym adresie), wiersze posortowane po adresie, budynki bez numeru osobno.
 */
import type { CaseNumbersResponse } from '../services/api';
import { excelCsv } from './download';

export interface CaseNumberRow {
  address: string;
  caseNumber: string;
  year: number | null;
  current: boolean;
  status: string | null;
}

const byAddress = (a: string, b: string) => a.localeCompare(b, 'pl', { numeric: true, sensitivity: 'base' });

export function caseNumberRows(data: CaseNumbersResponse): { rows: CaseNumberRow[]; withoutNumber: string[] } {
  const buildings = [...data.buildings].sort((a, b) => byAddress(a.address, b.address));
  const seen = new Set<string>();
  const rows: CaseNumberRow[] = [];
  const withoutNumber: string[] = [];
  for (const b of buildings) {
    if (b.caseNumbers.length === 0) { withoutNumber.push(b.address); continue; }
    for (const c of b.caseNumbers) {
      if (seen.has(c.caseNumber)) continue;
      seen.add(c.caseNumber);
      rows.push({ address: b.address, caseNumber: c.caseNumber, year: b.year, current: b.current, status: c.status });
    }
  }
  return { rows, withoutNumber };
}

/** Tekst do schowka (np. do maila): „adres — numer (rok)", na końcu budynki bez numeru. */
export function caseNumbersText(rows: CaseNumberRow[], withoutNumber: string[]): string {
  const lines = rows.map((r) => `${r.address} — ${r.caseNumber}${r.year != null ? ` (${r.year})` : ''}`);
  if (withoutNumber.length > 0) {
    if (lines.length) lines.push('');
    lines.push('Bez numeru sprawy CEEB:', ...withoutNumber);
  }
  return lines.join('\n');
}

/** Same numery, jeden w wierszu — do wklejenia np. w wyszukiwarkę CEEB albo arkusz. */
export function caseNumbersOnly(rows: CaseNumberRow[]): string {
  return rows.map((r) => r.caseNumber).join('\n');
}

/** CSV (Excel, `;`): wiersz = numer; budynki bez numeru na końcu z pustym numerem. */
export function caseNumbersCsv(rows: CaseNumberRow[], withoutNumber: string[]): string {
  return excelCsv([
    ['Adres', 'Numer sprawy CEEB', 'Rok', 'Status w CEEB'],
    ...rows.map((r) => [r.address, r.caseNumber, r.year ?? '', r.status ?? '']),
    ...withoutNumber.map((a) => [a, '', '', 'brak numeru sprawy']),
  ]);
}
